import React, {useEffect, useState} from "react";
import PropTypes, {object} from "prop-types";
import CardItem from "./CardItem";
import SoftBox from "../../../components/SoftBox";
import {
    downloadFileFromUrl, getTitleWithDate, getTitleWithoutDate,
    prepareDownload
} from "../../../utlis/generalUtils";
import Pagination from "@mui/material/Pagination";
import {MenuItem, Select} from "@mui/material";
import DownloadDigitizedArticleDialog from "../../../layouts/publications/DownloadDigitizedArticleDialog";
import PreviewCard from "../../../layouts/publications/components/PreviewCard";
import {deleteFile, downloadFile, getExportedDocuments, scheduleDownload} from "../../../axios-client";
import {useSoftUIController} from "../../../context";
import DocumentDownloadSnackbar from "../../Snackbar/DocumentDownloadSnackbar";
import {io} from 'socket.io-client';
import WelcomeCard from "./WelcomeCard";
import { useTranslation } from "react-i18next";

const CardComponent = ({
                           data,
                           count,
                           size,
                           page,
                           userCreditBalance,
                           handlePageChange,
                           handleRowsPerPageChange,
                           loadingData,
                           getArchivesBySelectedDocument,
                           setNotification,
                           searchText,
                           refreshList,
                           handleOpenDialog,
                           displayWelcomeCard = false
                       }) => {
    const [controller] = useSoftUIController();
    const {userId, role} = controller;
    const { t } = useTranslation('common');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const initialExpandedSections = {
        "Extracted Text(WORD)": false,
        "Translated Text(WORD)": false,
        "Summary(WORD)": false,
        "Bookmarked PDF": false,
        "Audio": false,
        "XML": false,
        "EPUB": false,
        "Searchable PDF": false,
        "HOCR": false
    }
    const [selectedDigitization, setSelectedDigitization] = useState({id: null, title: "", picture_path: "", digitization_results: [], services: []});
    const [expandedSections, setExpandedSections] = useState(initialExpandedSections);
    const initialSelectedFields = {
        "Extracted Text(WORD)": false,
        "Translated Text(WORD)": false,
        "Summary(WORD)": false,
        "Bookmarked PDF": false,
        "Audio": false,
        "XML": false,
        "EPUB": false,
        "Searchable PDF": false,
        "HOCR": false,
        "SINGLE_HTML": false,
        "MULTIPLE_HTML": false,
    }
    const [selectedFields, setSelectedFields] = useState(initialSelectedFields);
    const [documents, setDocuments] = useState([]);
    const [eventNameList, setEventNameList] = useState([]);
    const key = `document_to_download_by_${role}_${userId}`

    useEffect(() => {
        fetchStoredDocuments();
    }, []);

    useEffect(() => {
        const baseURL = window?.appConfig?.REACT_APP_SOCKET_SERVER || process.env.REACT_APP_SOCKET_SERVER;
        const socket = io(baseURL, {
            path: "/socket.io"
        });

        eventNameList.forEach((event) => {
            socket.on(event, () => {
                setEventNameList((prevEventNames) => prevEventNames.filter((eventName) => eventName !== event));
                fetchStoredDocuments();
            });
        });

        return () => {
            eventNameList.forEach((event) => {
                socket.off(event);
            });
            socket.disconnect();
        };
    }, [eventNameList]);

    const fetchStoredDocuments = () => {
        const storedDocuments = JSON.parse(localStorage.getItem(key)) || [];
        if (storedDocuments.length > 0) {
            getExportedDocuments({ titles: storedDocuments })
                .then(res => {
                    setDocuments(res.data.documents);
                })
                .catch(e => {
                    console.error("Failed to fetch documents:", e);
                });
        }
    };

    const handlePaginationSizeChange = (event) => {
        let newSize = parseInt(event.target.value, 10);
        handleRowsPerPageChange(newSize);
    };

    const handlePaginationPageChange = (event, newPage) => {
        const numberOfPages = Math.ceil((count ?? data.length) / size);
        let page = newPage <= numberOfPages ? newPage : 1;
        handlePageChange(page);
    };

    const handleOpenModal = async (id, title, picture_path, digitization_results, services) => {
        setIsModalOpen(true);
        setSelectedDigitization({id: id, title: title, picture_path: picture_path, digitization_results: digitization_results, services: services})
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedDigitization(null)
        setSelectedFields(initialSelectedFields);
    };

    const handleFieldChange = (field) => {
        setSelectedFields((prev) => ({
            ...prev,
            [field]: !prev[field]
        }));
    };


    const updateDocumentToDownloadList = (documentTitle) => {
        const baseTitle = documentTitle.split("_").slice(0, -1).join("_");
        const date = documentTitle.split("_").pop();
        const existingDocuments = JSON.parse(localStorage.getItem(key)) || [];
        if (selectedFields["Extracted Text(WORD)"]) {
            existingDocuments.push(`${baseTitle}_extracted_text_${date}`);
        }
        if (selectedFields["EPUB"]) {
            existingDocuments.push(`${baseTitle}_epub_${date}`);
        }
        if (selectedFields["XML"]) {
            existingDocuments.push(`${baseTitle}_xml_zip_${date}`);
        }
        if (selectedFields["Split HTML (File-by-File)"]) {
            existingDocuments.push(`${baseTitle}_html_zip_${date}`);
        }
        if (selectedFields["Combined HTML"]) {
            existingDocuments.push(`${baseTitle}_html_${date}`);
        }
        if (selectedFields["HOCR"]) {
            existingDocuments.push(`${baseTitle}_hocr_zip_${date}`);
        }
        if (selectedFields["Searchable PDF"]) {
            existingDocuments.push(`${baseTitle}_searchable_${date}`);
        }
        if (selectedFields["Translated Text(WORD)"]) {
            existingDocuments.push(`${baseTitle}_translation_${date}`);
        }
        if (selectedFields["Summary(WORD)"]) {
            existingDocuments.push(`${baseTitle}_summary_${date}`);
        }
        if (selectedFields["Bookmarked PDF"]) {
            existingDocuments.push(`${baseTitle}_bookmarked_${date}`);
        }
        if (selectedFields["Audio"]) {
            existingDocuments.push(`${baseTitle}_audio_${date}`);
        }
        localStorage.setItem(key, JSON.stringify(existingDocuments));
        fetchStoredDocuments()
    }

    const handleGenerateDocument = async () => {
        handleCloseModal();
        let documentTitle = getTitleWithDate(selectedDigitization.title)
        const baseTitle = documentTitle.split("_").slice(0, -1).join("_");
        const date = documentTitle.split("_").pop();

        if (selectedFields["Extracted Text(WORD)"]) {
            await setEventNameList((prevEventNames) => [
                ...prevEventNames,
                `document_to_download_${baseTitle}_extracted_text_${date}`
            ]);
        }
        if (selectedFields["EPUB"]) {
            await setEventNameList((prevEventNames) => [
                ...prevEventNames,
                `document_to_download_${baseTitle}_epub_${date}`
            ]);
        }
        if (selectedFields["MULTIPLE_HTML"]) {
            await setEventNameList((prevEventNames) => [
                ...prevEventNames,
                `document_to_download_${baseTitle}_html_zip_${date}`
            ])
        }
        if (selectedFields["SINGLE_HTML"]) {
            await setEventNameList((prevEventNames) => [
                ...prevEventNames,
                `document_to_download_${baseTitle}_html_${date}`
            ])
        }
        if (selectedFields["XML"]) {
            await setEventNameList((prevEventNames) => [
                ...prevEventNames,
                `document_to_download_${baseTitle}_xml_zip_${date}`
            ]);
        }
        if (selectedFields["HOCR"]) {
            await setEventNameList((prevEventNames) => [
                ...prevEventNames,
                `document_to_download_${baseTitle}_hocr_zip_${date}`
            ]);
        }
        if (selectedFields["Searchable PDF"]) {
            await setEventNameList((prevEventNames) => [
                ...prevEventNames,
                `document_to_download_${baseTitle}_searchable_${date}`
            ]);
        }
        if (selectedFields["Translated Text(WORD)"]) {
            await setEventNameList((prevEventNames) => [
                ...prevEventNames,
                `document_to_download_${baseTitle}_translation_${date}`
            ]);
        }
        if (selectedFields["Summary(WORD)"]) {
            await setEventNameList((prevEventNames) => [
                ...prevEventNames,
                `document_to_download_${baseTitle}_summary_${date}`
            ]);
        }
        if (selectedFields["Bookmarked PDF"]) {
            await setEventNameList((prevEventNames) => [
                ...prevEventNames,
                `document_to_download_${baseTitle}_bookmarked_${date}`
            ]);
        }
        if (selectedFields["Audio"]) {
            await setEventNameList((prevEventNames) => [
                ...prevEventNames,
                `document_to_download_${baseTitle}_audio_${date}`
            ]);
        }
        
        scheduleDownload(selectedDigitization.id, documentTitle, selectedFields).then(res => {
            updateDocumentToDownloadList(documentTitle)
        }).catch(e => {
            console.error("Failed to schedule download:", e);
        })
    };

    const handleOpenPreview = async (id) => {
        const selectedDocumentData = await getArchivesBySelectedDocument(id);
        if (selectedDocumentData) {
            setPreviewData(selectedDocumentData);
            setIsPreviewOpen(true);
        }
    };

    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        setPreviewData(null);
        setExpandedSections(initialExpandedSections)
    };

    const toggleSection = (section) => {
        setExpandedSections((prev) => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const downloadSearchableFile = async (id) => {
        const selectedDocumentData = await getArchivesBySelectedDocument(id);
        if (selectedDocumentData) {
            downloadFileFromUrl(selectedDocumentData.filename, selectedDocumentData.searchable_pdf_path)
        }
    };

    const handleDownload = (doc) => {

        downloadFile({ title: doc.title })
            .then((response) => {
                prepareDownload([response.data], response.data.type, getTitleWithoutDate(doc.title))
            })
            .then(() => {
                deleteDocumentFile(doc.title);
            })
            .catch((error) => {
                console.error("Error downloading document:", error);
            });
    };

    const deleteDocumentFromLocalStorage = (documentTitle) => {
        let existingDocuments = JSON.parse(localStorage.getItem(key)) || [];
        existingDocuments = existingDocuments.filter(doc => doc !== documentTitle);
        localStorage.setItem(key, JSON.stringify(existingDocuments));

        setDocuments(prevDocuments =>
            prevDocuments.filter(document => document.title !== documentTitle)
        );

    };

    const deleteDocumentFile = (documentTitle) => {

        deleteDocumentFromLocalStorage(documentTitle);
        deleteFile(documentTitle)
            .then((result) => {
            })
            .catch((error) => {
                console.error('Error deleting file:', error);
            });
    };

    return (
        <React.Fragment>
            {data && data.map((element, index) => {
                return (
                    <SoftBox key={index} style={{paddingBottom: "10px"}}>
                        <CardItem
                            id={element.id}
                            feedback_rating={element.feedback_rating}
                            title={element.title}
                            subjects={element.subjects}
                            author={element.author}
                            sourceLanguage={element.source_language}
                            targetLanguage={element.target_language}
                            status={element.status}
                            origin={element.origin}
                            services_data={element.services_data}
                            totalApproximativeTime={element.totalApproximativeTime}
                            digitizationStartedAt={element.digitizationStartedAt}
                            all_services_selected={element.all_services_selected}
                            paymentStatus={element.paymentStatus}
                            freeAgentDocuments={element.freeAgentDocuments}
                            image={element.picture_path}
                            publishedAt={element.publishedAt}
                            createdAt={element.createdAt}
                            summary={element.summary}
                            searchText={searchText}
                            handleClickDownloadDigitizedDocument={() => handleOpenModal(element.id, element.title, element.picture_path, element.digitization_results, element.services_data)}
                            handleOpenPreview={() => handleOpenPreview(element.id)}
                            downloadSearchableFile={() => downloadSearchableFile(element.id)}
                            getArchivesBySelectedDocument={getArchivesBySelectedDocument}
                            setNotification={setNotification}
                            articleId={element.articleId}
                            digitizationProcesses={element.digitizationProcesses}
                            pendingProcessesCost={element.pendingProcessesCost}
                            CompletedProcessesCost={element.CompletedProcessesCost}
                            cost={element.cost}
                            userCreditBalance={userCreditBalance}
                            refreshList={refreshList}
                            digitizerId={element.digitizerId}
                        />
                    </SoftBox>
                );
            })}
            {data.length > 0 ? (
                <SoftBox key={"pagination-card"}>
                    <SoftBox>
                        <SoftBox style={{display: "flex"}}>
                            <SoftBox style={{marginLeft: "auto"}}>
                                <Select
                                    id="rowsPerPage"
                                    fullWidth
                                    value={size}
                                    onChange={handlePaginationSizeChange}
                                    style={{
                                        width: "100%",
                                        padding: "10px 0px",
                                        border: "none",
                                        fontSize: "14px",
                                        boxSizing: "border-box",
                                        outline: "none",
                                        fontFamily: "Arial, Roboto",
                                    }}
                                    inputProps={{"aria-label": "Without label"}}
                                >
                                    <MenuItem key={"5"} value="5">5</MenuItem>
                                    <MenuItem key={"10"} value="10" selected={true}>10</MenuItem>
                                    <MenuItem key={"20"} value="20">20</MenuItem>
                                </Select>
                            </SoftBox>
                            <SoftBox>
                                <Pagination
                                    size="sm"
                                    color="primary"
                                    count={Math.ceil(count / size)}
                                    page={page}
                                    onChange={handlePaginationPageChange}
                                    showFirstButton
                                    showLastButton
                                    style={{marginLeft: "auto"}}
                                />
                            </SoftBox>
                        </SoftBox>
                    </SoftBox>
                </SoftBox>
            ) : loadingData ? (
                <p style={{textAlign: "center", padding: "50px"}}> Loading... </p>
            ) : displayWelcomeCard ?
                (
                    <WelcomeCard
                        handleOpenDialog={handleOpenDialog}
                    />
                ) :
                <p style={{ textAlign: 'center', padding: '50px' }}> {t('data.noResultFound')}</p>
            }

            <DownloadDigitizedArticleDialog
                isModalOpen={isModalOpen}
                handleCloseModal={handleCloseModal}
                docData={selectedDigitization}
                selectedFields={selectedFields}
                handleFieldChange={handleFieldChange}
                handleGenerateDocument={handleGenerateDocument}
            />

            <PreviewCard
                isPreviewOpen={isPreviewOpen}
                handleClosePreview={handleClosePreview}
                previewData={previewData}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
            />

            <DocumentDownloadSnackbar
                documents={documents} handleDownload={handleDownload} deleteFile={deleteDocumentFile} userId={userId}
                onClose={deleteDocumentFromLocalStorage}
            />

        </React.Fragment>
    );
};

CardComponent.propTypes = {
    data: PropTypes.arrayOf(object),
    count: PropTypes.number,
    size: PropTypes.number,
    page: PropTypes.number,
    userCreditBalance: PropTypes.number,
    loadingData: PropTypes.bool,
    displayWelcomeCard: PropTypes.bool,
    searchText: PropTypes.string,
    handlePageChange: PropTypes.func,
    handleSizeChange: PropTypes.func,
    handleClickOpen: PropTypes.func,
    handleRowsPerPageChange: PropTypes.func,
    getArchivesBySelectedDocument: PropTypes.func,
    setNotification: PropTypes.func,
    refreshArticles: PropTypes.func,
    refreshList: PropTypes.func,
    handleOpenDialog: PropTypes.func,
};

export default CardComponent;
